import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { apiService } from '../services/api.service';
import { useOrg } from './OrgContext';

interface Event {
  id: string;
  name: string;
  event_type: string;
  slug?: string;
  date_start?: string;
  date_end?: string;
  status?: string;
  lanes_count?: number;
  contest_enabled?: boolean; // Admin LogFit : section concours dans les emails d'inscription
  // Organisation de l'ÉVÉNEMENT — jamais celle de l'utilisateur connecté.
  // C'est elle qui nomme la room temps réel (LOG-201) : un admin qui assiste un
  // client est exactement le cas où les deux diffèrent.
  organization_id?: string;
}

interface EventContextType {
  events: Event[];
  selectedEventId: string | null;
  selectedEvent: Event | null;
  setSelectedEventId: (id: string | null) => void;
  loading: boolean;
  refreshEvents: () => Promise<void>;
}

const EventContext = createContext<EventContextType | undefined>(undefined);

const STORAGE_KEY = 'selectedEventId';

export const EventProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventIdState] = useState<string | null>(() => {
    // Un lien venu d'un mail (« Voir les bénévoles », récap) nomme son
    // événement dans `?event=` : il prime sur le dernier choisi, sinon le lien
    // ouvrirait la bonne page sur le mauvais événement.
    const depuisLien = new URLSearchParams(window.location.search).get('event');
    if (depuisLien) {
      localStorage.setItem(STORAGE_KEY, depuisLien);
      return depuisLien;
    }
    return localStorage.getItem(STORAGE_KEY);
  });
  const [loading, setLoading] = useState(true);
  const { workingOrgId, setWorkingOrgId, isAdmin, loading: orgLoading } = useOrg();

  // L'événement nommé par le lien, à consommer une seule fois : un admin qui
  // travaille sur l'organisation A et clique une notification (« nouvel
  // inscrit ») visant un événement de l'organisation B doit atterrir SUR cet
  // événement. Sans ça, la liste filtrée sur A ne le contient pas, le contexte
  // retombe sur le premier événement de A, et le panneau demandé par le lien
  // (`?team=`) ne s'ouvre jamais (constat de Bryan sur staging, 2026-09-27).
  const evenementDuLien = useRef<string | null>(
    new URLSearchParams(window.location.search).get('event')
  );

  const loadEvents = async () => {
    try {
      setLoading(true);
      const response = await apiService.getEvents();
      const eventsList = response.data.events || response.data || [];

      // Un lien nomme son événement, donc son organisation : elle prime sur
      // l'organisation de travail sauvegardée. On attend que l'OrgContext ait
      // fini de charger, sinon il reposerait son choix sauvegardé par-dessus
      // le nôtre. Changer d'organisation relance ce chargement (effet
      // ci-dessous), qui trouvera alors l'événement dans sa liste.
      if (isAdmin && !orgLoading && evenementDuLien.current) {
        const cible = eventsList.find((e: Event) => e.id === evenementDuLien.current);
        evenementDuLien.current = null;
        if (cible?.organization_id && cible.organization_id !== workingOrgId) {
          setWorkingOrgId(cible.organization_id);
          return;
        }
      }
      // Filtrer pour n'afficher que les événements actifs (pas archivés)
      // Et, pour un admin qui a choisi une organisation de travail, ne montrer
      // que les événements de CETTE organisation : le sélecteur d'événement
      // traverse toutes les organisations, ce qui rend la liste illisible dès
      // qu'on travaille pour un client précis.
      const activeEvents = eventsList
        .filter((e: Event) => e.status !== 'archived')
        .filter((e: Event) => !workingOrgId || e.organization_id === workingOrgId);
      setEvents(activeEvents);

      const savedEventId = localStorage.getItem(STORAGE_KEY);

      // Si l'événement sélectionné n'existe plus, sélectionner le premier
      if (savedEventId && !activeEvents.find((e: Event) => e.id === savedEventId)) {
        if (activeEvents.length > 0) {
          setSelectedEventIdState(activeEvents[0].id);
          localStorage.setItem(STORAGE_KEY, activeEvents[0].id);
        } else {
          setSelectedEventIdState(null);
          localStorage.removeItem(STORAGE_KEY);
        }
      }
      // Si aucun événement n'est sélectionné, sélectionner le premier par défaut
      else if (!savedEventId && activeEvents.length > 0) {
        setSelectedEventIdState(activeEvents[0].id);
        localStorage.setItem(STORAGE_KEY, activeEvents[0].id);
      }
    } catch (error) {
      console.error('Erreur chargement événements:', error);
    } finally {
      setLoading(false);
    }
  };

  // Rechargé quand l'admin change d'organisation de travail : la liste change
  // de périmètre, et l'événement sélectionné peut ne plus en faire partie —
  // `loadEvents` retombe alors sur le premier de la nouvelle liste.
  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingOrgId, orgLoading]);

  const setSelectedEventId = (id: string | null) => {
    setSelectedEventIdState(id);
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const selectedEvent = events.find(e => e.id === selectedEventId) || null;

  return (
    <EventContext.Provider value={{
      events,
      selectedEventId,
      selectedEvent,
      setSelectedEventId,
      loading,
      refreshEvents: loadEvents
    }}>
      {children}
    </EventContext.Provider>
  );
};

export const useEvent = (): EventContextType => {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error('useEvent must be used within an EventProvider');
  }
  return context;
};
